// Unpublished Work © 2026 Deere & Company.
import React from 'react';
import IconAlertInfo from '@deere/icons/IconAlertInfo';
import {Button, Link, Tooltip, Typography, useTheme} from '@deere/isg.component-library';
import {translationStore} from '../stores/translation-store';
import {useIsMobile} from '../helpers/use-is-mobile-helper';

const WINDOWS = 'Windows';
const STELLAR_SUPPORT_PLACEHOLDER = '{0}';
const STELLAR_SUPPORT_HREF = 'https://StellarSupport.deere.com';
const STELLAR_SUPPORT_TEXT = 'StellarSupport.deere.com';

function getDownLoadLink(internal: boolean): string {
    return internal ? '/i/download-software-manager/win' : '/download-software-manager/win';
}

function isSupportedOS(osName: string | undefined): boolean {
    return osName === WINDOWS;
}

export const SoftwareManagerPage = ({osName, internal}: {osName: string | undefined; internal: boolean}) => {
    const buttonLink = isSupportedOS(osName) ? getDownLoadLink(internal) : null;
    const shouldShowButton = buttonLink !== null;
    const bullets = [
        translationStore.SOFTWARE_MANAGER_DESCRIPTION_LINE_1,
        translationStore.SOFTWARE_MANAGER_DESCRIPTION_LINE_2,
        translationStore.SOFTWARE_MANAGER_DESCRIPTION_LINE_3
    ];
    const theme = useTheme();
    const isMobile = useIsMobile(499);

    return (
        <div className='flex m-4' data-testid='download-page'>
            <div className='bg-white p-4 max-w-[700px] rounded-lg' data-testid='content'>
                <Typography variant='h1'>{translationStore.tool_john_deere_software_manager}</Typography>
                {!shouldShowButton && (
                    <div
                        className={`rounded p-3 mt-4 flex items-center gap-2`}
                        style={{backgroundColor: theme.jddcTokens.JddcColorBackgroundInfoSubtle}}
                        data-testid='unsupported-message'
                    >
                        <IconAlertInfo size={20} color={theme.jddcTokens.JddcColorIconInfo} />
                        <Typography variant='bodyMedium'>{translationStore.SOFTWARE_MANAGER_WINDOWS_ONLY}</Typography>
                    </div>
                )}
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-6 mt-4`} data-testid='inner-content'>
                    <div className='flex-1' data-testid='description'>
                        <Typography variant='labelLarge' className='mb-3 font-semibold'>
                            {translationStore.SOFTWARE_MANAGER_DESCRIPTION_HEADER}
                        </Typography>
                        {isMobile && (
                            <div className='w-[150px] h-[120px] mx-auto my-4' data-testid='image-container-mobile'>
                                <img
                                    alt={translationStore.SOFTWARE_MANAGER_DESCRIPTION_HEADER}
                                    src={'/assets/DownloadsUpdates-Graphic.png'}
                                    className='w-full h-full object-cover'
                                />
                            </div>
                        )}
                        <ul className='list-disc pl-5 my-2'>
                            {bullets.map((text: string, index: number) => (
                                <li key={`bullet-${index}`} className='mb-1'>
                                    <Typography variant='bodyMedium'>
                                        {text.includes(STELLAR_SUPPORT_PLACEHOLDER) ?
                                            <>
                                                {text.split(STELLAR_SUPPORT_PLACEHOLDER)[0]}
                                                <Link
                                                    href={STELLAR_SUPPORT_HREF}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                >
                                                    {STELLAR_SUPPORT_TEXT}
                                                </Link>
                                                {text.split(STELLAR_SUPPORT_PLACEHOLDER)[1]}
                                            </>
                                        :   text}
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                        <Typography variant='bodySmall' color={theme.jddcTokens.JddcColorTextSecondary}>
                            {translationStore.SOFTWARE_MANAGER_FOOTER}
                        </Typography>
                    </div>
                    {!isMobile && (
                        <div className='w-[150px] h-[120px] shrink-0' data-testid='image-container'>
                            <img
                                alt={translationStore.SOFTWARE_MANAGER_DESCRIPTION_HEADER}
                                src={'/assets/DownloadsUpdates-Graphic.png'}
                                className='w-full h-full object-cover'
                            />
                        </div>
                    )}
                </div>
                {shouldShowButton ?
                    <a className='mt-4 block' href={buttonLink} download data-testid='button-container'>
                        <Button variant='primary'>{translationStore.SOFTWARE_MANAGER_DOWNLOAD}</Button>
                    </a>
                :   <div className='mt-4' data-testid='disabled-download-container'>
                        <Tooltip title={translationStore.NOT_COMPATIBLE_TOOLTIP} placement='top' variant='dark'>
                            <span>
                                <Button variant='primary' disabled={true}>
                                    {translationStore.SOFTWARE_MANAGER_DOWNLOAD}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                }
            </div>
        </div>
    );
};
